import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {GridColumnVisibilityModel} from '@mui/x-data-grid';
import {GridInitialStateCommunity} from '@mui/x-data-grid/models/gridStateCommunity';
import {flexeMuiTheme} from '../shared/mui/default-mui-theme';
import {FlexeDataGrid} from '../shared/mui/flexe-data-grid/FlexeDataGrid';
import {
  LocationContentsGridLocationContent,
  getLocationContentsColumns
} from '../shared/mui/flexe-data-grid/location-contents-grid/locationContentsGridColumns';
import LocationContentsGridHelpers from '../shared/mui/flexe-data-grid/location-contents-grid/LocationContentsGridHelpers';
import ItemMasterService from '../shared/services/ItemMasterService';
import InventoryService from '../shared/services/InventoryService';

interface LocationContentsTableProps {
  locationId: number;
  itemMasterService: ItemMasterService;
  inventoryService: InventoryService;
}
export function LocationContentsTable({locationId, itemMasterService, inventoryService}: LocationContentsTableProps) {
  const [rows, setRows] = useState<LocationContentsGridLocationContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const columns = useMemo(() => getLocationContentsColumns('/wh'), []);

  useEffect(() => {
    let isCancelled = false;

    async function fetchAllContents() {
      setIsLoading(true);
      try {
        const helpers = new LocationContentsGridHelpers(inventoryService, itemMasterService);
        const gridData = await helpers.fetchAllLocationContents(locationId);

        if (isCancelled) {
          return;
        }

        setRows(gridData);
        setIsLoading(false);
      } catch (error) {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchAllContents();

    return () => {
      isCancelled = true;
    };
  }, [locationId, inventoryService, itemMasterService]);

  const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
    const enableParentLpnColumn = rows.some(({parentLpnBarcode}) => parentLpnBarcode);
    const enableLpnColumn = rows.some(({lpnBarcode}) => lpnBarcode);
    const enableUomColumn = rows.some(({uomJson}) => uomJson);
    const enableLotCodeColumn = rows.some(({lotCode}) => lotCode);
    const enableExpirationDateColumn = rows.some(({expirationDate}) => expirationDate);
    const enableManufactureDateColumn = rows.some(({manufactureDate}) => manufactureDate);
    const model = {
      parentLpnBarcode: enableParentLpnColumn,
      lpnBarcode: enableLpnColumn,
      uomJson: enableUomColumn,
      lotCode: enableLotCodeColumn,
      expirationDate: enableExpirationDateColumn,
      manufactureDate: enableManufactureDateColumn
    };
    if (isLoading) {
      // Hide all columns until we know which ones to show
      for (const column of columns) {
        model[column.field] = false;
      }
    }
    return model;
  }, [columns, rows, isLoading]);

  const initialState: GridInitialStateCommunity = {
    sorting: {
      // By default, sort by parent LPN so that way nested LPNs are grouped together by parent.
      sortModel: [{field: 'parentLpnBarcode', sort: 'asc'}]
    }
  };

  // HACK: Manually do an implied secondary sort on LPN barcode. Multi-sorting is a paid feature. https://mui.com/x/react-data-grid/sorting/#multi-sorting
  // Remove this and add a real secondary sort when we switch to paid.
  const sortedRows = useMemo(() => [...rows.sort((a, b) => (a.lpnBarcode ?? '').localeCompare(b.lpnBarcode ?? ''))], [
    rows
  ]);

  return (
    <ThemeProvider theme={flexeMuiTheme}>
      <FlexeDataGrid
        sx={{height: '720px'}}
        columns={columns}
        rows={sortedRows}
        loading={isLoading}
        columnVisibilityModel={columnVisibilityModel}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        isRowSelectable={() => false}
        initialState={initialState}
      />
    </ThemeProvider>
  );
}
